import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, RotateCcw, Save } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { buildTagDataFromDetail, tagMaterials } from '@/components/jewellery/JewelleryTagViewer';

const IN = 'w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-[12px] focus:border-indigo-500 focus:outline-none';
const FIELDS = [['name', 'classification_schema'], ['note', 'issue_note'], ['sieves', 'sieves'], ['pieces', 'pieces']];
const valOf = (m, fld) => (fld === 'pieces' ? Number(m[fld] || 0) : String(m[fld] || ''));

const Row = ({ r, e, o, set, i }) => {
  const zero = r.material_type === 'diamond' && Number(r.carats || 0) > 0 && !Number(e.pieces);
  return (
  <tr className={`border-t border-slate-100 align-top ${zero ? 'bg-red-50' : ''}`} data-testid={`tag-fix-row-${i}`}>
    <td className="py-1.5 pr-2 text-[11px] font-semibold text-slate-500">{r.code || '—'}{zero ? <div className="mt-0.5 text-[10px] font-bold text-red-600" data-testid={`tag-fix-zero-${i}`}>0 pieces</div> : null}</td>
    {FIELDS.map(([k]) => (
      <td key={k} className="py-1.5 pr-2">
        <input
          type={k === 'pieces' ? 'number' : 'text'} min={k === 'pieces' ? 0 : undefined} value={e[k]}
          onChange={(ev) => set(i, k, k === 'pieces' ? Math.max(0, parseInt(ev.target.value || '0', 10)) : ev.target.value)}
          className={`${IN} ${k === 'pieces' ? 'w-16' : ''} ${o && String(o[k] ?? '') !== String(e[k]) ? 'border-amber-400 bg-amber-50' : ''}`}
          data-testid={`tag-fix-${k}-${i}`}
        />
        {o && String(o[k] ?? '') !== String(e[k]) ? <div className="mt-0.5 text-[10px] text-slate-400">portal: {String(o[k] ?? '') || '—'}</div> : null}
      </td>
    ))}
    <td className="py-1.5 text-right text-[12px] tabular-nums text-slate-700">{Number(r.carats || 0).toFixed(2)}</td>
  </tr>
  );
};

// Super admin: correct a tag's lines (name, note, sieves, pieces) without touching the portal.
export function TagLineFixDialog({ data, onClose, onSaved }) {
  const raw = data._raw;
  const fix = raw.tag_fix || {};
  const rows = useMemo(() => (raw.rm_consumed_snapshot || []).filter((m) => m.material_type !== 'gold').map((m, i) => ({ ...m, i, code: String(m.classification_raw || m.classification_schema || '').trim().toUpperCase() })), [raw]);
  const [edits, setEdits] = useState(() => rows.map((m) => Object.fromEntries(FIELDS.map(([k, fld]) => [k, valOf(m, fld)]))));
  const [skip, setSkip] = useState(!!fix.skip_rules);
  const [join, setJoin] = useState(() => new Set(raw.tag_join || []));
  const [busy, setBusy] = useState(false);
  const set = (i, k, v) => setEdits((p) => p.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  // Names used on 2+ lines that stay separate by default (every line on manual tags, colour stones on portal tags).
  const joinable = useMemo(() => {
    const cnt = {};
    rows.forEach((r, i) => {
      if (!raw.is_manual && r.material_type !== 'color_stone') return;
      const n = String(edits[i].name || '').trim().replace(/\s+/g, ' ').toLowerCase();
      if (n) cnt[n] = (cnt[n] || 0) + 1;
    });
    return Object.entries(cnt).filter(([, c]) => c > 1);
  }, [rows, edits, raw.is_manual]);
  const toggleJoin = (n) => setJoin((s) => { const x = new Set(s); if (x.has(n)) x.delete(n); else x.add(n); return x; });

  const preview = useMemo(() => {
    let n = -1;
    const rm = (raw.rm_consumed_snapshot || []).map((m) => {
      if (m.material_type === 'gold') return m;
      n += 1;
      const e = edits[n];
      return { ...m, classification_schema: e.name, tag_display_name: '', issue_note: e.note, sieves: e.sieves, pieces: e.pieces };
    });
    return tagMaterials(buildTagDataFromDetail({ ...raw, rm_consumed_snapshot: rm, tag_fix: { ...fix, skip_rules: skip }, tag_join: [...join] }), 'expanded');
  }, [raw, edits, skip, fix, join]);

  const save = async () => {
    if (edits.some((e) => !String(e.name).trim())) { toast.error('Tag display name cannot be blank.'); return; }
    const lines = rows.map((r, i) => {
      const o = fix.orig?.[i] || {};
      const d = Object.fromEntries(FIELDS.map(([k]) => [k, edits[i][k]]).filter(([k, v]) => String(o[k] ?? '') !== String(v)));
      return { i, code: r.code, ...d };
    }).filter((l) => Object.keys(l).length > 2);
    setBusy(true);
    try { await notesApi.put(`/notes/tag-workspace/admin/line-fix/${raw.id}`, { lines, skip_rules: skip, join_names: joinable.map(([n]) => n).filter((n) => join.has(n)) }); toast.success('Tag corrected.'); onSaved(); } catch (e) { toast.error(extractError(e, 'Could not save the correction')); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try { await notesApi.delete(`/notes/tag-workspace/admin/line-fix/${raw.id}`); toast.success('Correction removed — portal details restored.'); onSaved(); } catch (e) { toast.error(extractError(e, 'Could not remove the correction')); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[260] flex items-center justify-center p-4" data-testid="tag-fix-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-4 shadow-xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">Correct tag lines — {data.jewelleryNo}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="tag-fix-close"><X size={18} /></button>
        </div>
        <p className="mb-3 text-[12px] text-slate-500">Raw portal lines. Give lines the same tag display name to merge them ({raw.is_manual ? 'on this manual tag' : 'for colour stones'}, also tick "Join" below). Carats are locked. Changes apply everywhere on this portal (never the main portal) until the piece comes back from repair; past sales and approvals are not changed.</p>
        <table className="w-full text-left">
          <thead><tr className="text-[10px] uppercase tracking-wide text-slate-400"><th>Code</th><th>Tag display name</th><th>Note</th><th>Sieves</th><th>Pieces</th><th className="text-right">Carats</th></tr></thead>
          <tbody>{rows.map((r, i) => <Row key={i} r={r} e={edits[i]} o={fix.orig?.[i]} set={set} i={i} />)}</tbody>
        </table>
        <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-700">
          <input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} data-testid="tag-fix-skip-rules" />
          Don&apos;t apply tag rules to this tag (show exactly what is typed above)
        </label>
        {joinable.length ? (
          <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-2" data-testid="tag-fix-join">
            <div className="mb-1 text-[11px] font-semibold text-indigo-900">Same-name lines stay separate on {raw.is_manual ? 'manual tags' : 'colour stones'}. Join them on this tag only:</div>
            {joinable.map(([n, c]) => (
              <label key={n} className="flex items-center gap-2 text-[12px] text-slate-700">
                <input type="checkbox" checked={join.has(n)} onChange={() => toggleJoin(n)} data-testid={`tag-fix-join-${n.replace(/\s+/g, '-')}`} />
                Join the {c} <b className="uppercase">{n}</b> lines into one
              </label>
            ))}
          </div>
        ) : null}
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid="tag-fix-preview">
          <div className="mb-1 text-[11px] font-semibold text-slate-600">Tag will show (Expanded)</div>
          {preview.map((m) => (
            <div key={m.key} className="flex justify-between text-[12px]"><span className="font-semibold">{m.classification}{m.note ? <span className="font-normal text-slate-500"> · {m.note}</span> : null}</span><span className="tabular-nums">{Number(m.carats).toFixed(2)} ct{m.hidePieces || !Number(m.pieces) ? '' : ` · ${m.pieces} pcs`}</span></div>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          {fix.active ? <button onClick={reset} disabled={busy} className="flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-[12px] font-semibold text-slate-700" data-testid="tag-fix-reset"><RotateCcw size={13} /> Restore portal details</button> : null}
          <button onClick={save} disabled={busy} className="flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1.5 text-[12px] font-semibold text-white" data-testid="tag-fix-save">{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save correction</button>
        </div>
      </div>
    </div>
  );
}


// Diamond lines printed on the tag that have carats but 0 pieces (pieces hidden by a rule are ignored).
export function zeroPieceCount(item) {
  return tagMaterials(buildTagDataFromDetail(item), 'expanded')
    .filter((m) => !m.hidePieces)
    .reduce((n, m) => n + (m.partInfo || []).filter((p) => p.dia && p.carats > 0 && !p.pieces).length, 0);
}

export const openLineFix = (id) => window.dispatchEvent(new CustomEvent('tagws-open-line-fix', { detail: id }));

// Opens the correction screen for any piece (cards, filter, dashboard widget).
export function TagLineFixHost() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const on = (e) => notesApi.post('/notes/tag-workspace/tags/batch', { jewellery_ids: [e.detail] })
      .then(({ data: res }) => { if (res?.items?.[0]) setData(buildTagDataFromDetail(res.items[0])); })
      .catch((err) => toast.error(extractError(err, 'Could not load the tag')));
    window.addEventListener('tagws-open-line-fix', on);
    return () => window.removeEventListener('tagws-open-line-fix', on);
  }, []);
  if (!data) return null;
  return <TagLineFixDialog data={data} onClose={() => setData(null)} onSaved={() => { setData(null); window.dispatchEvent(new Event('tagws-pieces-changed')); }} />;
}
